import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useStores } from '../../context/context';
import RepatchButton from '../../components/RepatchButton';
import RepatchInput from '../../components/RepatchInput';
import Modal from '../../components/Modal';
import sharedStyles from '../../styles/shared.module.css';
import styles from './Tickets.module.css';
import Quote from './components/Quote';
import { observer } from 'mobx-react';
import LoadingAnimation from '../../components/LoadingAnimation';
import { useAnalytics } from '../../context/analytics-context';
import { isValidEmail } from '../../helpers/misc';
import PromoCodeInput from '../../components/PromoCodeInput';
import PickupTime from './PickupTime';
import PromoCode from '../../components/PromoCode';
import klarnaImage from '../../assets/images/klarna.png';
import starImage from '../../assets/images/star.png';
import whatsappIcon from '../../assets/images/whatsapp.png';
// import calendarImage from '../../assets/images/calendar.png';

function TicketPage() {
  const { ticketStore } = useStores();
  const navigate = useNavigate();
  const location = useLocation();
  const analytics = useAnalytics();
  const ticket = ticketStore.currentTicket;
  const [errorState, setErrorState] = useState(false);
  const [promo, setPromo] = useState({
    title: '',
  });

  const [modalState, setModalState] = useState({
    open: false,
    email: '',
    error: '',
  });

  const [nextError, setNextError] = useState('');

  const updateModalState = (key, value) => {
    setModalState((prev) => {
      return { ...prev, [key]: value };
    });
  };

  useEffect(() => {
    const getTicket = async (ticketID) => {
      await ticketStore.getTicket(ticketID);
      if (!ticketStore.hasFailed) {
        // ticket is read directly from store (observer will re-render)
      } else {
        alert(ticketStore.errorMessage);
        ticketStore.clearTicket();
        navigate('/');
      }
    };

    if (location.search) {
      const params = new URLSearchParams(location.search);
      getTicket(params.get('ticketID'));
    } else if (!ticket) {
      navigate('/');
    }

    analytics.getActivePromo().then((promo) => {
      setPromo(promo);
    });
  }, []);

  useEffect(() => {
    if (ticket) {
      if (!ticket.technician) {
        setErrorState(
          'Sorry, no technicians are currently available in your area for that fix.'
        );
      } else if (ticket.quotes.length === 0) {
        setErrorState('Sorry, we cannot quote for that fix online.');
      }
      ticket.quotes.forEach((quote) => {
        if (quote.accepted) {
          ticketStore.setQuoteID(quote.quoteID);
        }
      });
    }
  }, [ticket]);

  const repatchMyDevice = async () => {
    setNextError('');
    await ticketStore.repatchMyDevice();
    if (!ticketStore.hasFailed) {
      analytics.logEvent('begin_checkout', {
        currency: 'GBP',
        items: [
          {
            item_id: ticketStore.currentTicket.ticketID,
          },
        ],
      });
      navigate('/ticket/pay');
    } else {
      setNextError(ticketStore.errorMessage || 'An unknown error occurred. Please try again.');
    }
  };

  const saveQuote = async () => {
    if (modalState.email.length > 0 && isValidEmail(modalState.email)) {
      await ticketStore.saveTicket(modalState.email);
      if (!ticketStore.hasFailed) {
        alert(
          `Your quote is saved. Your reference number is: RE/${ticketStore.ticketID}`
        );
        setModalState({
          email: '',
          open: false,
          error: '',
        });
      }
    } else {
      updateModalState('error', 'Please enter a valid email address');
    }
  };

  const selectQuote = (quoteID) => {
    ticketStore.setQuoteID(quoteID);
    repatchMyDevice();
  };

  if (!ticket) {
    return <LoadingAnimation />;
  } else {
    return (
      <div className={styles.page}>
        <PromoCode promo={promo} />
        <div className={sharedStyles.container}>
          <div className={styles.ticketPageHeading}>
            {ticket.technician && (
              <div
                className={styles.profilePic}
                style={{
                  backgroundImage: `url("${process.env.REACT_APP_API_URL}${ticket.technician.imgUrl}")`,
                }}
              ></div>
            )}
            <h1>Your Quote</h1>
            {ticket.technician && (
              <>
                <h2>
                  {ticket.technician.firstname} {ticket.technician.surname}
                </h2>
                <p className='small'>
                  {ticket.technician.firstname} {ticket.technician.surname} will
                  be fixing your device
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {Array.from({
                    length: Math.round(ticket.technician.rating || 5),
                  })
                    .fill(0)
                    .map((_, idx) => (
                      <img
                        key={idx}
                        src={starImage}
                        alt='star'
                        style={{ width: 28, height: 28, display: 'block' }}
                      />
                    ))}
                </div>
              </>
            )}
          </div>

          <div className={styles.quotesContainer}>
            {ticket.quotes.map((quote) => {
              return (
                <Quote
                  ticket={ticket}
                  quote={quote}
                  key={quote.quoteID}
                  onClick={() => selectQuote(quote.quoteID)}
                  isSelected={ticketStore.quoteID === quote.quoteID}
                />
              );
            })}
          </div>
          <div>
            <img
              src={klarnaImage}
              alt="Klarna"
              style={{ width: '100%', margin: '10px 0 0px 0' }}
            />
          </div>
          <PromoCodeInput ticket={ticket} />
        </div>
        <div className={styles.slotCard}>
          <div className={styles.slotContent}>
            <PickupTime ticket={ticket} />
          </div>
        </div>
        <div className={styles.buttonContainer}>
          {nextError && (
            <p className={sharedStyles.error} style={{ textAlign: 'center' }}>{nextError}</p>
          )}
          <RepatchButton
            label='SAVE QUOTE'
            onClick={() => updateModalState('open', true)}
            style={{ flex: 1, margin: '20px 5px 0 5px' }}
            disabled={ticketStore.isFetching}
          />
          <RepatchButton
            label='NEXT'
            onClick={repatchMyDevice}
            style={{ flex: 1, margin: '20px 5px 0 5px' }}
            disabled={ticketStore.isFetching}
          />
        </div>
        <Modal
          isOpen={modalState.open}
          closeModal={() => updateModalState('open', false)}
        >
          <h2>Save Your Quote</h2>
          <p className='small'>
            Need some time to think? No problem, just pop in your email address
            and we'll send you a copy of your quote.
          </p>
          <p className='small' style={{ padding: '1rem 0' }}>
            Or, if you have any questions give us a call on{' '}
            <a href='tel:07475 703912'>07475 703912</a>
          </p>

          <RepatchInput
            label='Email Address'
            placeholder='youremail@address.com'
            disabled={ticketStore.isFetching}
            value={modalState.email}
            onChange={(event) => updateModalState('email', event.target.value)}
            onFocus={() => updateModalState('error', '')}
          />
          <p className={styles.error}>{modalState.error}</p>
          <RepatchButton
            label='Save Quote'
            disabled={ticketStore.isFetching}
            onClick={saveQuote}
          />
        </Modal>
        <Modal
          isOpen={!!errorState}
          closeModal={() => {
            setErrorState(false);
            navigate('/orders');
          }}
        >
          <h3 style={{ paddingBottom: 20 }}>Give us a Call</h3>

          <p className='large' style={{ paddingBottom: 20 }}>
            {(() => {
              if (!ticket) return errorState;

              const deviceID = ticket.deviceID;
              const repairTypeID = ticket.repairTypeID;

              const isOtherDevice = deviceID != null && Number(deviceID) === -1;
              const isOtherRepair = repairTypeID != null && Number(repairTypeID) === -1;

              if (isOtherDevice || isOtherRepair) {
                const deviceName =
                  ticket.otherDevice ||
                  ticket.deviceName ||
                  ticket.device?.device ||
                  'your device';
                return `We need to investigate your ${deviceName} repair before getting back to you with a quote, but feel free to WhatsApp us.`;
              }
              return errorState;
            })()}
          </p>
          <p style={{ paddingBottom: 20 }}>
            Your quote ref: RE/{ticket?.ticketID || 'N/A'}
          </p>
          <a
            href="https://wa.me/447475703912"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              backgroundColor: '#25D366',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              textDecoration: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <img 
              src={whatsappIcon} 
              alt="WhatsApp" 
              style={{ width: 24, height: 24 }} 
            />
            <span>Chat on WhatsApp</span>
          </a>
        </Modal>
      </div>
    );
  }
}

export default observer(TicketPage);
